import typography from "@tailwindcss/typography";
import type { Config } from "tailwindcss";
import plugin from "tailwindcss/plugin";
import animate from "tailwindcss-animate";
import typographyConfig from "./typography.config";
import { untitledTokens } from "./untitled-tokens";

const withAlpha = <T extends Record<string, unknown>>(tokens: T): T =>
	Object.fromEntries(
		Object.entries(tokens).map(([name, value]) => [
			name,
			typeof value === "string" && /^var\(--[\w-]+\)$/.test(value)
				? `color-mix(in srgb, ${value} calc(<alpha-value> * 100%), transparent)`
				: value,
		]),
	) as T;

export const config: Config = {
	darkMode: ["class"],
	content: [
		"./node_modules/@repo/design-system/components/**/*.{ts,tsx}",
		"./node_modules/@repo/design-system/lib/**/*.{ts,tsx}",
		"./node_modules/@repo/design-system/index.tsx",
		"./components/**/*.{ts,tsx}",
		"./lib/**/*.{ts,tsx}",
		"./app/**/*.{ts,tsx}",
		"./providers/**/*.{ts,tsx}",
		"!./lib/db/**",
	],
	theme: {
		container: {
			center: true,
			padding: "2rem",
			screens: {
				"2xl": "1400px",
			},
		},
		extend: {
			colors: {
				...withAlpha(untitledTokens.colors),
				// shadcn color layer removed — all UI is on Untitled UI tokens.
				// Quadrant-specific custom tokens (sidebar/matrix/icon/badge/table) kept.
				sidebar: {
					DEFAULT: "hsl(var(--sidebar-background))",
					foreground: "hsl(var(--sidebar-foreground))",
					primary: "hsl(var(--sidebar-primary))",
					"primary-foreground": "hsl(var(--sidebar-primary-foreground))",
					accent: "hsl(var(--sidebar-accent))",
					"accent-foreground": "hsl(var(--sidebar-accent-foreground))",
					border: "hsl(var(--sidebar-border))",
					ring: "hsl(var(--sidebar-ring))",
				},
				matrix: {
					high: "hsl(var(--matrix-high) / <alpha-value>)",
					negative: "hsl(var(--matrix-negative) / <alpha-value>)",
					border: "hsl(var(--matrix-border) / <alpha-value>)",
				},
				icon: {
					DEFAULT: "hsl(var(--icon) / <alpha-value>)",
				},
				badge: {
					text: "hsl(var(--badge-text) / <alpha-value>)",
				},
				table: {
					cell: "hsl(var(--table-cell) / <alpha-value>)",
				},
			},
			fontFamily: {
				sans: [
					"var(--font-archivo)",
					"Archivo",
					"var(--font-inter)",
					"Inter",
					"var(--font-roobert)",
					"Roobert",
					"system-ui",
					"-apple-system",
					"BlinkMacSystemFont",
					"Segoe UI",
					"Helvetica Neue",
					"Arial",
					"sans-serif",
				],
				mono: [
					"var(--font-roobert)",
					"Roobert",
					"ui-monospace",
					"SFMono-Regular",
					"Menlo",
					"Monaco",
					"Consolas",
					"Liberation Mono",
					"Courier New",
					"monospace",
				],
				crimson: [
					"var(--font-crimson-pro)",
					"Crimson Pro",
					"Georgia",
					"Cambria",
					"Times New Roman",
					"Times",
					"serif",
				],
			},
			keyframes: {
				"accordion-down": {
					from: { height: "0" },
					to: { height: "var(--radix-accordion-content-height)" },
				},
				"accordion-up": {
					from: { height: "var(--radix-accordion-content-height)" },
					to: { height: "0" },
				},
				"collapsible-down": {
					from: { height: "0" },
					to: { height: "var(--radix-collapsible-content-height)" },
				},
				"collapsible-up": {
					from: { height: "var(--radix-collapsible-content-height)" },
					to: { height: "0" },
				},
			},
			animation: {
				"accordion-down": "accordion-down 0.2s ease-out",
				"accordion-up": "accordion-up 0.2s ease-out",
				"collapsible-down": "collapsible-down 0.2s ease-out",
				"collapsible-up": "collapsible-up 0.2s ease-out",
			},
			typography: typographyConfig,
			backgroundColor: withAlpha(untitledTokens.backgroundColor),
			textColor: withAlpha(untitledTokens.textColor),
			borderColor: withAlpha(untitledTokens.borderColor),
			ringColor: withAlpha(untitledTokens.ringColor),
			outlineColor: withAlpha(untitledTokens.outlineColor),
			gradientColorStops: withAlpha(untitledTokens.gradientColorStops),
			textDecorationColor: withAlpha(untitledTokens.textDecorationColor),
			ringOffsetColor: withAlpha(untitledTokens.ringOffsetColor),
			boxShadow: untitledTokens.boxShadow,
			fontSize: untitledTokens.fontSize,
			spacing: {
				"4.5": "1.125rem",
			},
			underlineOffset: {
				"3": "3px",
			},
		},
	},
	plugins: [
		animate,
		typography,
		// Untitled UI v4-only utilities/variants backported for Tailwind v3.
		plugin(({ addUtilities, addVariant }) => {
			addUtilities({
				".transition-inherit-all": { "transition-property": "inherit" },
				// v4 renamed v3's `outline-none` (transparent, a11y-friendly outline)
				// to `outline-hidden`. Backport so upstream UUI components work as-is.
				".outline-hidden": {
					outline: "2px solid transparent",
					"outline-offset": "2px",
				},
			});
			addVariant("data-loading", "&[data-loading]");
			addVariant("data-icon-only", "&[data-icon-only]");
			addVariant("data-icon", "&[data-icon]");
			addVariant("data-text", "&[data-text]");
			addVariant("data-icon-leading", "&[data-icon-leading]");
			addVariant("not-last", "&:not(:last-child)");
			addVariant("selected", "&[data-selected]");
			addVariant(
				"group-selected/button-group",
				":merge(.group\\/button-group)[data-selected] &",
			);
		}),
	],
};
